/* Dependency Imports */
import { useState } from 'react';
import { flexRender, getCoreRowModel, getSortedRowModel, useReactTable } from '@tanstack/react-table';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import {
  Box,
  InputBase,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Toolbar,
  CircularProgress,
  IconButton,
  Typography,
  Button,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import Skeleton from '@mui/material/Skeleton';
import { FlexBetween } from '../../utils/CommonStyles';
import { IUser } from '../../types/user';

/* Project Imports */

const StandardTable = (props: ChildProps) => {
  const { columns, data, loading, handleGlobalFilterValue, count, download, width, maxHeight, title, color } = props;
  /* Hooks */
  const table = useReactTable({
    columns,
    data,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    autoResetExpanded: false,
  });

  /* States */

  const [searchValue, setSearchValue] = useState<string>('');

  /* Queries/Mutations */

  /* Functions */

  const removeFilter = () => {
    setSearchValue('');
    handleGlobalFilterValue('');
  };

  return (
    <TableContainer component={Paper} elevation={8} sx={maxHeight ? { maxHeight } : {}}>
      <Toolbar sx={{ width: '100%', px: '16px !important' }}>
        <FlexBetween sx={{ alignItems: 'center', display: 'flex', width: '100% ' }}>
          {handleGlobalFilterValue ? (
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' } }}>
              <InputBase
                sx={{ borderBottom: '1px solid #000' }}
                value={searchValue}
                onKeyPress={(event) => {
                  if (event.key === 'Enter') {
                    handleGlobalFilterValue(searchValue);
                    setSearchValue('');
                  }
                }}
                onChange={(e) => {
                  setSearchValue(e.target.value); // Set undefined to remove the filter entirely
                }}
                placeholder={`${count} records...`}
                inputProps={{ 'aria-label': 'search' }}
              />
              <Box sx={{ mt: { xs: 1, sm: 0 }, display: 'flex' }}>
                <SearchIcon
                  onClick={() => handleGlobalFilterValue(searchValue)}
                  sx={{
                    height: '100%',
                    mx: { xs: 0, sm: 1.5 },
                    cursor: handleGlobalFilterValue ? 'pointer' : 'auto',
                  }}
                />
                <IconButton
                  className="std-table-remove-filter"
                  onClick={() => {
                    removeFilter();
                  }}
                  sx={{ cursor: handleGlobalFilterValue ? 'pointer' : 'auto' }}
                >
                  <FilterAltOffIcon />
                </IconButton>
              </Box>
            </Box>
          ) : title ? (
            <Typography variant="h2">
              <strong>{title}</strong>
            </Typography>
          ) : (
            <Box></Box>
          )}
          {data.length ? 
            <Box sx={{ ml: { xs: 3, sm: 0 } }}>
              <Button
                sx={{
                  backgroundColor: color,
                  color: '#fff',
                  mr: 1,
                  ':hover': {
                    backgroundColor: color,
                    color: '#fff',
                  },
                  fontFamily: 'Abadi-Standard !important',
                }}
                onClick={() => download('floorplans')}
              >
                Download All Floorplans
              </Button>
              <Button
                sx={{
                  backgroundColor: color,
                  color: '#fff',
                  ':hover': {
                    backgroundColor: color,
                    color: '#fff',
                  },
                  fontFamily: 'Abadi-Standard !important',
                }}
                onClick={() => download('hotlist')}
              >
                Download Hotlist
              </Button>
            </Box>
          : null}
        </FlexBetween>
      </Toolbar>
      <Table sx={{ minWidth: width ? 'auto' : 650, position: 'relative' }}>
        <TableHead sx={{ position: 'sticky', top: 0, zIndex: 1 }}>
          {table.getHeaderGroups().map((headerGroup: any) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header: any) => (
                <TableCell key={header.id} sx={{ backgroundColor: color, color: '#fff', fontFamily: 'Abadi-Standard !important' }}>
                  {header.isPlaceholder ? null : (
                    <Box
                      sx={{ cursor: header.column.getCanSort() ? 'pointer' : 'auto', display: 'flex' }}
                      onClick={header.column.getToggleSortingHandler()}
                      title={
                        header.column.getCanSort()
                          ? header.column.getNextSortingOrder() === 'asc'
                            ? 'Sort ascending'
                            : header.column.getNextSortingOrder() === 'desc'
                            ? 'Sort descending'
                            : 'Clear sort'
                          : undefined
                      }
                    >
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      {{
                        asc: <KeyboardArrowUpIcon sx={{ alignSelf: 'center' }} />,
                        desc: <KeyboardArrowDownIcon />,
                      }[header.column.getIsSorted() as string] ?? null}
                    </Box>
                  )}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableHead>
        {loading ? (
          <TableBody>
            <TableRow>
              <TableCell colSpan={columns.length} sx={{ textAlign: 'center' }}>
                <div>
                  <CircularProgress />
                </div>
                <div>
                  <em>Loading...</em>
                </div>
              </TableCell>
            </TableRow>
          </TableBody>
        ) : (
          <TableBody>
            {table.getRowModel().rows.length > 0 ? (
              <>
                {table.getRowModel().rows.map((row: any) => {
                  return (
                    <TableRow key={row.id}>
                      {row.getVisibleCells().map((cell: any) => {
                        return loading ? (
                          <TableCell key={cell.id}>
                            <Skeleton animation="wave" variant="text" height={30} />
                          </TableCell>
                        ) : (
                          <TableCell sx={{ fontFamily: 'Abadi-Standard !important' }}>
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })}
              </>
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} sx={{ textAlign: 'center' }}>
                  No Results Found. Try again!
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        )}
      </Table>
    </TableContainer>
  );
};

/* Types */

interface ChildProps {
  columns: any;
  data: any;
  loading?: any;
  handleGlobalFilterValue?: any;
  count?: number;
  download?: any;
  width?: any;
  maxHeight?: string;
  user?: IUser | null;
  title?: string;
  color: string;
}

/* GQL */

export default StandardTable;
